import React, { useState } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { it } from "date-fns/locale";
import { format, parse, isValid } from "date-fns";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

/**
 * Italian date picker — drop-in replacement for <input type="date" />.
 * - Props:
 *   value: "YYYY-MM-DD" or "" (ISO storage format, matches backend)
 *   onChange: (newIsoString) => void
 *   placeholder, disabled, minDate, maxDate, className, "data-testid"
 *   clearable: bool (default true)
 * - Displays formatted as "dd/MM/yyyy" with Italian months.
 */
export const DatePickerIT = ({
  value,
  onChange,
  placeholder = "gg/mm/aaaa",
  disabled,
  minDate,
  maxDate,
  className,
  clearable = true,
  "data-testid": testId,
}) => {
  const [open, setOpen] = useState(false);
  // Parse "YYYY-MM-DD" → Date (local, no TZ shift)
  const selected = (() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    const d = parse(value.slice(0, 10), "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  })();

  const handleSelect = (d) => {
    if (!d) { onChange(""); setOpen(false); return; }
    // Output as ISO date string YYYY-MM-DD (matches backend)
    onChange(format(d, "yyyy-MM-dd"));
    setOpen(false);
  };

  const display = selected ? format(selected, "dd/MM/yyyy") : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal bg-white border-stone-300 hover:bg-stone-50",
            !selected && "text-stone-400",
            className,
          )}
          data-testid={testId || "datepicker-it-trigger"}
        >
          <CalendarIcon className="w-4 h-4 mr-2 text-stone-500 shrink-0" />
          <span className="flex-1 truncate">{display || placeholder}</span>
          {clearable && selected && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange(""); } }}
              className="ml-2 inline-flex text-stone-400 hover:text-stone-700 cursor-pointer"
              aria-label="Pulisci"
              data-testid={testId ? `${testId}-clear` : "datepicker-it-clear"}
            >
              <X className="w-3 h-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          locale={it}
          disabled={(d) => {
            if (minDate && d < minDate) return true;
            if (maxDate && d > maxDate) return true;
            return false;
          }}
          defaultMonth={selected || new Date()}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerIT;
